import { useSyncExternalStore } from 'react';

import { CAPTCHA_BYPASS_TOGGLE_KEY } from '@/constants/captcha';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { getCountryCodeServerSnapshot, readCountryCode, subscribeCountryCode } from '@/store/country-code-store';
import { isCaptchaExemptCountry } from '@/utils/should-bypass-captcha';

interface UseCaptchaBypassOptions {
  // Opt-out por instância (usado por forms sensíveis, ex.: redeem de cupom). Quando true:
  // nunca dispensamos o CAPTCHA e não consultamos a feature toggle — fica zero overhead.
  disabled?: boolean;
}

interface UseCaptchaBypassReturn {
  bypassCaptcha: boolean;
  isResolving: boolean;
}

// SCRUM-1576: decide se o CAPTCHA deve ser dispensado combinando o país do usuário (RU/CN/TR)
// com a feature toggle remota. O país vem do external store via `useSyncExternalStore`, então o
// hook re-renderiza quando o /user/getIp resolve depois do mount (corrige o race). A toggle só é
// consultada para países isentos, evitando chamadas à API para o restante do tráfego. `isResolving`
// indica que aguardamos a toggle para um país isento — usado para não exibir e remover o widget logo após.
export function useCaptchaBypass({ disabled = false }: UseCaptchaBypassOptions = {}): UseCaptchaBypassReturn {
  const countryCode = useSyncExternalStore(subscribeCountryCode, readCountryCode, getCountryCodeServerSnapshot);
  const isExemptCountry = isCaptchaExemptCountry(countryCode);
  const shouldEvaluateBypass = isExemptCountry && !disabled;
  const { isEnabled, isLoading } = useFeatureToggle({ key: CAPTCHA_BYPASS_TOGGLE_KEY, autoFetch: shouldEvaluateBypass });

  return {
    bypassCaptcha: shouldEvaluateBypass && isEnabled,
    isResolving: shouldEvaluateBypass && isLoading,
  };
}
